import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useParams, NavLink } from 'react-router-dom';
import { DateTime } from 'luxon';

import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Tooltip from '@material-ui/core/Tooltip';
import Divider from '@material-ui/core/Divider';
import Button from '@material-ui/core/Button';
import Link from '@material-ui/core/Link';

import AddIcon from '@material-ui/icons/Add';
import AssignmentIcon from '@material-ui/icons/Assignment';

import EditEntryDialog from 'app/components/form/EditEntryDialog';
import Loading from 'app/components/loaders/Loading';

import useApi from 'app/context/useApi';

import { paper } from 'app/styles/core';
import { container } from 'app/styles/new-layout';

const useStyles = makeStyles(theme => ({
    container: {
        ...container(theme),
        padding: theme.spacing(3),
        minHeight: '80vh',
        position: 'relative'
    },
    paper: {
        ...paper(theme),
    },
    marginBottom: {
        marginBottom: theme.spacing(3)
    },
    marginBottomSmall: {
        marginBottom: theme.spacing(1)
    },
    entriesContainer: {
        paddingTop: theme.spacing(3)
    },
    entry: {
        textAlign: 'center',
        borderRadius: theme.shape.borderRadius,
        boxShadow: '0px 0px 10px 1px #bbbbbb',
        height: '100%',
        paddingTop: theme.spacing(3),
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
        paddingBottom: theme.spacing(12),
        position: 'relative',
        transition: theme.transitions.create(),
        cursor: 'pointer',
        '&:hover': {
            boxShadow: '0px 0px 24px -2px #bbbbbb',
        }
    },
    iconContainer: {
        marginBottom: theme.spacing(2)
    },
    title: {
        lineHeight: 1.3
    },
    entryBottom: {
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        paddingBottom: theme.spacing(3),
    },
    empty: {
        paddingTop: theme.spacing(5),
        paddingBottom: theme.spacing(5),
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3)
    },
    badge: {
        position: 'absolute',
        left: '-8px',
        top: '8px',
        borderRadius: '30px',
        padding: '4px 14px',
        color: '#ffffff',
    },
    badgeactive: {
        backgroundColor: theme.palette.primary.main
    },
    badgeaccepted: {
        backgroundColor: theme.palette.success.main
    },
    badgerejected: {
        backgroundColor: theme.palette.error.main
    }
}));

const newEntry = {
    id: 0,
    values: [],
    user_id: 0
}

export default function ViewForm(props) {

    let params = useParams();
    const classes = useStyles();
    const api = useApi();

    const [ form, setForm ] = useState(null);

    const [ activeEntry, setActiveEntry ] = useState(newEntry);
    const [ editEntryDialogOpen, setEditEntryDialogOpen ] = useState(false);

    const [ loading, setLoading ] = useState(false);

    // Getters ------------------------------------------

    const getForm = () => {
        setLoading(true);

        api.get(`/form/${params.id}`)
            .then(res => setForm(res.form))
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        getForm();
    }, []);

    // Dialog Functions

    const openEditEntryDialog = (entry) => {
        setActiveEntry(entry);
        setEditEntryDialogOpen(true);
    }

    const closeEditEntryDialog = () => {
        setEditEntryDialogOpen(false);
    }

    const closeEditEntryDialogAndRefresh = () => {
        closeEditEntryDialog();
        getForm();
    }

    const editEntryDialogClosed = () => {
        setActiveEntry(newEntry);
    }

    return (
        <div className={classes.container}>
            <Loading isLoading={loading}/>
            { form &&
                <>
                    <Grid
                        container
                        spacing={1}
                        justify="space-between"
                        alignItems="center"
                        className={classes.marginBottom}
                        >
                        <Grid item>
                            <Typography color="secondary" variant="h4" component="h2">
                                {form.name}
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Button
                                to={`/forms`}
                                variant={'outlined'}
                                component={NavLink}
                                color="secondary"
                                >
                                Back to All entries
                            </Button>
                        </Grid>
                    </Grid>
                    <Grid
                        container
                        spacing={1}
                        justify="space-between"
                        alignItems="center"
                        className={classes.marginBottomSmall}
                        >
                        <Grid item>
                            <Typography color="textSecondary" variant="h5" component="h4">
                                Your Submitted Entries
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Tooltip
                                title={(form.entries && form.entries.length >= form.entries_per_user) ? 'You have reached your maximum amount of entries.' : `Add ${form.button_name}`}
                                >
                                <span>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        startIcon={<AddIcon/>}
                                        onClick={() => openEditEntryDialog(newEntry)}
                                        disabled={(form.entries && form.entries.length >= form.proposals_per_user)}
                                        >
                                        Add {form.button_name}
                                    </Button>
                                </span>
                            </Tooltip>
                        </Grid>
                    </Grid>
                    <Divider />
                    {(form.entries && form.entries.length) ? (
                        <Grid container spacing={3} className={classes.entriesContainer}>
                            {form.entries.map(entry => (
                                <Grid item key={entry.id} xs={12} sm={6} md={4} lg={3}>
                                    <div
                                        className={classes.entry}
                                        onClick={() => openEditEntryDialog(entry)}
                                        >
                                        <div className={classes.iconContainer}>
                                            <AssignmentIcon fontSize='large' color="action"/>
                                        </div>
                                        <Typography
                                            variant='h6'
                                            color='secondary'
                                            gutterBottom
                                            className={classes.title}
                                            >
                                            {entry.title ?? `Entry ${entry.id}`}
                                        </Typography>
                                        {entry.subtitle &&
                                            <Typography variant='body1' color='textSecondary'>
                                                {entry.subtitle}
                                            </Typography>
                                        }
                                        <div className={classes.entryBottom}>
                                            <Typography variant='body2' gutterBottom color='textSecondary'>
                                                Submitted On:
                                            </Typography>
                                            <Typography variant='subtitle2' color='textSecondary'>
                                                {DateTime.fromISO(entry.created_at).toLocaleString(DateTime.DATETIME_MED)}
                                            </Typography>
                                        </div>
                                    </div>
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Typography
                            align="center"
                            color="textSecondary"
                            variant="subtitle2"
                            className={classes.empty}
                            >
                            You have not created any entries.<br/>
                            <Link onClick={() => openEditEntryDialog(newEntry)} color='secondary'>Click Here</Link> to create a new entry.
                        </Typography>
                    )}
                    <EditEntryDialog
                        form={form}
                        id={activeEntry.id}
                        open={editEntryDialogOpen}
                        onClose={closeEditEntryDialog}
                        onSave={closeEditEntryDialogAndRefresh}
                        onDelete={closeEditEntryDialogAndRefresh}
                        onClosed={editEntryDialogClosed}
                    />
                </>
            }
        </div>
    );
}
